
import Vue, { VueConstructor } from 'vue'
import AlertInfo from '@/components/AlertInfo.vue'
import Locale from '@/components/Locale.vue'
import SingleUploadCard from '@/components/SingleUploadCard.vue'
import { EventBus } from '@/plugins/eventBus'
import SubscriptionMixin from "@/mixins/SubscriptionMixin.vue"
import AlertError from "@/components/AlertError.vue"
import SubscriptionConfirmation from "@/components/subscription/SubscriptionConfirmation.vue"
import axios, { AxiosError } from "axios"


interface FormData {
  [key: string]: string|number|null
}

interface OCRResult {
  file: string,
  error: string,
  result: string
}

interface MappingCv {
  [key: string]: any
}

interface Agency {
  id: number,
  name: string,
  logo: string | null,
  prefix: string | null,
  ocr: number | null
}


export default (Vue as VueConstructor<Vue & InstanceType<typeof SubscriptionMixin>>).extend({
  name: 'SubscriptionDocuments',
  components: {
    AlertInfo,
    SingleUploadCard,
    AlertError,
    SubscriptionConfirmation,
    Locale
  },
  mixins: [SubscriptionMixin],
  props: {
    kiosk: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return{
      file: null as string|null,
      files: [],
      formData: {
        cv_document: null,
      } as FormData,
      waitRequest: false,
      rgpd: false,
      selectedSpace: {} as Agency,
      visibility: "",
      step: 1
    }
  },
  computed: {
    spaces() {
      let filteredOcrSpaces: Agency[] = []
      if (Array.isArray(this.agencies)) {
        this.agencies.forEach((space: Agency) => {
          if (space.ocr === 1) {
            filteredOcrSpaces.push(space)
          }
        })
      }
      return filteredOcrSpaces
    },
    consent(): boolean {
      return this.rgpd && (this.selectedSpace !== null && Object.keys(this.selectedSpace).length !== 0)
    }
  },
  methods: {
    showLoader() {
      this.waitRequest = true
      this.visibility = "hidden-vs"
    },
    filterAgencies(item: Agency, queryText: string, itemText: string) {
      const words = [...queryText.toLocaleLowerCase()]
      const items = [...itemText.toLocaleLowerCase()]

      if(words.length < 2){
        return itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
      } else {
        return words.every(word => items.indexOf(word) > -1)
      }
    },
    onFileUploaded(ref: keyof FormData, data: string, ocr: OCRResult) {
      (this.formData[ref] as string) = data
      if (ocr.file.includes('error')) {
        this.displaySnackbar(ocr.error, 'error')
        this.resetData()
      } else if (this.selectedSpace !== null) {
        if (ocr.result) {
          this.subscribeWorker(ocr, data)
        } else {
          this.resetData()
          this.errorSnackbar()
        }
      }
    },
    async downloadImage(url: string): Promise<Blob> {
      return axios.get(url, { responseType: 'blob' })
        .then((response) => {
          return response.data
        })
        .catch(() => {
          return null // non-blocking process
        })
    },
    async subscribeWorker(ocr: OCRResult, filename: string) {
      let body: FormData = {
        agency_id: this.selectedSpace.id,
        url: this.$route.params.agencySubDomain + '.dispojob.pro',
        cv_document: filename
      }

      // affect returned data from OCRisation
      JSON.parse(ocr.result).forEach((dataOcr : MappingCv) => {
        // Sanitize value from emoji characters because they're not supported by our Database
        Object.entries(dataOcr).forEach(([key, value]) => {
          if (typeof value === 'string') {
            // Remove emoji characters but preserve accented chars
            dataOcr[key] = value.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '')
          } else if (Array.isArray(value)) {
            dataOcr[key] = value.map((item) => {
              if (typeof item === 'string') {
                // Remove emoji characters but preserve accented chars
                return item.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '')
              }
              return String(item)
            })
          }
        })
        Object.assign(body, dataOcr)
      })

      // ensure to have atlease one of email or mobile_phone_number
      if (!body.email && !body.mobile_phone_number) {
        body.email = (body.cv_document as string).split('.')[0] + "@dispojob.com"
        body.mobile_phone_number = null
      }

      // Set default values for some fields
      if (!body.sex) {
        body.sex = 'M'
      }
      if (!body.first_name) {
        body.first_name = 'Non-détécté'
      }
      if (!body.last_name) {
        body.last_name = 'Non-détécté'
      }

      this.waitRequest = true
      
      // retrieve & upload extra documents if any
      await this.processExtraDocuments(body)
      
      // Try subscription with progressive fallbacks
      return this.trySubscription(body, filename)
    },
    
    async processExtraDocuments(body: FormData) {
      if (body.cv_url && body.cv_url != "") {
        const formData = new FormData()
        formData.append("files[]", await this.downloadImage(body.cv_url as string), 'CV anonymisé.jpeg')
        const response = await axios.post("/uploads", formData)
        if (response.data.success?.[0]) {
            body.anonymized_cv_document = response.data.success[0]
        }
      }
      if (body.photo_url && body.photo_url != "") {
        const formData = new FormData()
        formData.append("files[]", await this.downloadImage(body.photo_url as string), 'Photo.jpeg')
        const response = await axios.post("/uploads", formData)
        if (response.data.success?.[0]) {
            body.photo_document = response.data.success[0]
        }
      }
      if (body.profile_picture && body.profile_picture != "") {
        // Convert base64 to blob
        const base64Data = (body.profile_picture as string).split(',')[1]
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const photo = new Blob([byteArray], { type: 'image/jpeg' })

        const formData = new FormData()
        formData.append("files[]", photo, 'Photo.jpeg')
        const response = await axios.post("/uploads", formData)
        if (response.data.success && response.data.success[0]) {
          body.photo_document = response.data.success[0]
        }
        delete body.profile_picture
      }
    },
    
    async trySubscription(body: FormData, filename: string) {
      try {
        // 1st try subscription
        await axios.post('/v1/subscription/subscribe', body)
        // 1st try succeeded, reset after 10s
        this.step = 2
        setTimeout(() => { this.resetData() }, 10000)
        return
      } catch (error1) {
        // Fix invalid fields and retry
        this.fixInvalidFields(body, error1 as AxiosError)
        
        try {
          // 2nd try subscription
          await axios.post('/v1/subscription/subscribe', body)
          // 2nd try succeeded, reset after 10s
          this.step = 2
          setTimeout(() => { this.resetData() }, 10000)
          return
        } catch {
          // Final attempt with minimal data
          return this.finalSubscriptionAttempt(body, filename)
        }
      }
    },
    
    fixInvalidFields(body: FormData, error: AxiosError) {
      const errorMappings: {[key: string]: {field: string, value: string|null}} = {
        "Invalid sex": { field: "sex", value: "M" },
        "Invalid first_name": { field: "first_name", value: "Non-détécté" },
        "Invalid last_name": { field: "last_name", value: "Non-détécté" },
        "Invalid email": { field: "email", value: null },
        "Invalid mobile_phone_number": { field: "mobile_phone_number", value: null },
        "mobile_phone_number, email keys are missing": { field: "email", value: (body.cv_document as string).split('.')[0] + "@dispojob.com" },
        "Invalid birth_date": { field: "birth_date", value: null },
        "Invalid nationality": { field: "nationality", value: null },
        "Invalid postal_address": { field: "postal_address", value: null },
        "Invalid postal_city": { field: "postal_city", value: null },
        "Invalid postal_code": { field: "postal_code", value: null },
        "Invalid postal_country": { field: "postal_country", value: null },
        "Invalid introduction_text": { field: "introduction_text", value: null },
        "Invalid postal_complement": { field: "postal_complement", value: null },
        "Invalid birth_department": { field: "birth_department", value: null },
        "Invalid birth_city": { field: "birth_city", value: null },
        "Invalid birth_country": { field: "birth_country", value: null },
        "Invalid favorite_jobs": { field: "favorite_jobs", value: null },
        "Invalid transportation_means": { field: "transportation_means", value: null },
        "transportation_means must be an array": { field: "transportation_means", value: null },
        "Invalid nir": { field: "nir", value: null },
        "Invalid phone_number": { field: "phone_number", value: null },
        "Invalid child_in_charge": { field: "child_in_charge", value: null },
        "Invalid status": { field: "status", value: "available" },
        "Invalid state": { field: "state", value: "web" },
        "Invalid wage_category": { field: "wage_category", value: null },
        "Invalid payement_category": { field: "payement_category", value: null },
        "Invalid family_situation": { field: "family_situation", value: null },
        "Invalid inscription_origin": { field: "inscription_origin", value: null },
        "Invalid profile_picture": { field: "profile_picture", value: null },
        "availability preferences must be an array": { field: "availability_preferences", value: null },
        "invalid availability preferences": { field: "availability_preferences", value: null },
        "environment_preferences must be an array": { field: "environment_preferences", value: null },
        "invalid environment preferences": { field: "environment_preferences", value: null },
        "start_preferences must be an array": { field: "start_preferences", value: null },
        "invalid start preferences": { field: "start_preferences", value: null },
        "Invalid anonymized_cv_document": { field: "anonymized_cv_document", value: null },
        "Invalid photo_document": { field: "photo_document", value: null },
        "Invalid social_security_document": { field: "social_security_document", value: null },
        "Invalid identity_card_document": { field: "identity_card_document", value: null },
        "Invalid identity_card_verso_document": { field: "identity_card_verso_document", value: null },
        "Invalid resident_permit_document": { field: "resident_permit_document", value: null },
        "Invalid passport_document": { field: "passport_document", value: null },
        "Invalid other_identity_document": { field: "other_identity_document", value: null },
        "Invalid residence_proof_document": { field: "residence_proof_document", value: null },
        "Invalid last_work_certificate_document": { field: "last_work_certificate_document", value: null },
        "Invalid bank_identity_document": { field: "bank_identity_document", value: null },
        "Invalid aptitude_medical_examination_document": { field: "aptitude_medical_examination_document", value: null },
        "Invalid driver_licence_document": { field: "driver_licence_document", value: null },
        "Invalid caces_document": { field: "caces_documents", value: null },
        "Invalid habilitation_document": { field: "habilitation_documents", value: null },
        "Invalid licence_document": { field: "licence_documents", value: null },
        "Invalid other_document": { field: "other_documents", value: null },
        "Invalid diploma_document": { field: "diploma_documents", value: null },
        "Invalid bank_domiciliation": { field: "bank_domiciliation", value: null },
        "Invalid bank_beneficiary": { field: "bank_beneficiary", value: null },
        "Invalid bank_code": { field: "bank_code", value: null },
        "Invalid bank_wicket_code": { field: "bank_wicket_code", value: null },
        "Invalid bank_account_number": { field: "bank_account_number", value: null },
        "Invalid bank_key": { field: "bank_key", value: null },
        "Invalid address_position": { field: "address_position", value: null },
        "Invalid hard_skills_text": { field: "hard_skills_text", value: null },
        "Invalid soft_skills_text": { field: "soft_skills_text", value: null },
        "Invalid knowledge_text": { field: "knowledge_text", value: null },
        "Invalid project_text": { field: "project_text", value: null },
        "Invalid resume_keywords": { field: "resume_keywords", value: null }
      }
      
      // Iterate over the errorMappings and adjust the body fields
      Object.keys(errorMappings).forEach((errorKey) => {
        if (error.response?.data[0].includes(errorKey)) {
          body[errorMappings[errorKey].field] = errorMappings[errorKey].value
        }
      })
    },
    
    async finalSubscriptionAttempt(body: FormData, filename: string) {
      // Create a minimal subscription body
      let finalBody: {[key: string]: number|string|null} = {
        agency_id: this.selectedSpace.id,
        url: this.$route.params.agencySubDomain + '.dispojob.pro',
        cv_document: filename,
        sex: body.sex,
        first_name: body.first_name,
        last_name: body.last_name,
        mobile_phone_number: null,
        email: filename.split('.')[0] + "@dispojob.com"
      }
      
      if (body.anonymized_cv_document) {
        finalBody.anonymized_cv_document = body.anonymized_cv_document
      }
      if (body.photo_document) {
        finalBody.photo_document = body.photo_document
      }
      
      try {
        // 3rd try subscription
        await axios.post('/v1/subscription/subscribe', finalBody)
        // 3rd try succeeded, reset after 10s
        this.step = 2
        setTimeout(() => { this.resetData() }, 10000)
      } catch {
        // Full failure, redirect to standard subscription page
        this.errorSnackbar()
        setTimeout(() => { this.resetData() }, 3000)
      }
    },
    errorSnackbar(){
      let errorMessage = this.$vuetify.lang.t("$vuetify.subscription_page.error.saving")
      if (!this.kiosk) {
        EventBus.$emit('snackbar',{message: errorMessage + this.$vuetify.lang.t("$vuetify.subscription_page.error.express"), color: 'error'})
        setTimeout(() =>{this.$router.push(`/inscription/${this.$route.params.agencySubDomain}`)}, 2000)
      } else {
        EventBus.$emit('snackbar',{message: errorMessage, color: 'error'})
      }
    },
    displaySnackbar(message:string, color:string) {
      EventBus.$emit('snackbar',{message: message, color: color})
      this.resetData()
    },
    resetData() {
      if (this.spaces.length > 1) {
        this.selectedSpace = {} as Agency
      }
      this.rgpd = false
      this.formData['cv_document'] = null
      this.file = null
      this.files = []
      this.waitRequest = false
      this.visibility = ""
      this.step = 1
    }
  },
  watch: {
    agencies: function() {
      if (Array.isArray(this.agencies) && this.agencies.length === 1) {
        this.selectedSpace = (this.agencies[0] as Agency)
      }
    },
    spaces: {
      deep: true,
      handler: function (spaces) {
        if (spaces.length === 1) {
          this.selectedSpace = spaces[0]
        }
      }
    },
    selectedSpace: function() {
      let src = "dispojob_label.svg"
      if (this.selectedSpace.logo !== null && this.selectedSpace.logo !== undefined) {
        src = this.selectedSpace.logo
      }
      this.logo = ['development', 'test'].includes(process.env.NODE_ENV) ? require("@/assets/logos/" + src) : '/img/logos/' + src
    }
  }
})
